import {GeoJSON} from 'react-leaflet'
import * as React from 'react'
import {dataforvalterMapConfig} from "../../../../map/map-config";
import L from 'leaflet';
import ColorHandler from '../../../contract-features/ColorHandler';
import {DisplayFeature} from '../../domain';
import {GeometryObject} from "geojson";
import {PathOptions} from "leaflet";
import {RefObject} from "react";
import classNames = require("classnames");
import * as geojson from "geojson";

const colorHandler = new ColorHandler(true);

interface FeatureLayerProps {
    feature: DisplayFeature,
    data: GeometryObject,
    isSelected: boolean,
    cutMode: boolean,
    featureClicked: (latLng: L.LatLng) => void
}

export default class FeatureLayer extends React.Component<FeatureLayerProps> {

    private readonly ref: RefObject<GeoJSON>;

    constructor(props: FeatureLayerProps) {
        super(props);
        this.ref = React.createRef();
    }

    componentDidMount(): void {
        if (this.ref.current) {
            // XXX Feature ID is used to find selected features in DragSelect.tsx (getFeatureCoordinates)
            // Not the cleanest way to support seelct in map, but leaving it as is for now
            (this.ref.current.leafletElement as any).featureId = this.props.feature.id;
        }
    }

    componentDidUpdate(prevProps: Readonly<FeatureLayerProps>) {
        this.updateSVGPathClasses();
    }

    /**
     * Style function for Leaflet rendering.
     */
    private getStyle = (): PathOptions => {
        const {isSelected, cutMode} = this.props;
        let color = isSelected ? dataforvalterMapConfig.STYLES.highlightStyle.color : colorHandler.colorForFeatureType(this.props.feature.typeId);

        // Classes are only set by Leaflet on initial render, see updateSVGPathClasses() doc
        let classes = classNames({
            "marchingants": isSelected,
            "cutmode": cutMode
        });

        return {stroke: true,
            color: color,
            fillColor: color,
            opacity: isSelected ? dataforvalterMapConfig.STYLES.highlightStyle.opacity : dataforvalterMapConfig.STYLES.defaultStyle.opacity,
            fillOpacity: 0.9,
            weight: isSelected ? 7 : 5,
            className: classes
        };
    };

    /**
     * Update classes for Leaflet SVG path element after initial render. Leaflet has
     * a bug (https://github.com/leaflet/leaflet/issues/2662) where changes to the "className" field
     * in the style set is ignored after initial render.
     */
    updateSVGPathClasses() {
        const isSelected = this.props.isSelected;
        if (this.ref.current) {
            if (isSelected) {
                this.ref.current.leafletElement.bringToFront();
            }
            let layers = this.ref.current.leafletElement.getLayers();
            layers.forEach(l => this.setLayerClasses(l));
        }
    }

    /**
     * Update style for path in layer, and recursive for any sub layers.
     *
     * @param layer
     */
    private setLayerClasses(layer: L.Layer) {
        let path: Element = (layer as any)._path;
        if(!!path) {
            this.setPathClasses(path);
        } else {
            // For multipoint geometries, _layers is an ad-hoc object with ID as key and a layer for each point as value
            let layers: any = (layer as  any)._layers;
            if (layers && layers instanceof Object) {
                // Can't use e.g. Object.values() because it is not present on object
                for (let index in layers) {
                    if (layers.hasOwnProperty(index)) {
                        this.setLayerClasses(layers[index] as L.Layer)
                    }
                }
            }
        }
    }

    private setPathClasses(path: Element) {
        if (this.props.isSelected) {
            path.classList.add("marchingants");
        } else {
            path.classList.remove("marchingants");
        }

        if (this.props.cutMode) {
            path.classList.add("cutmode");
        } else {
            path.classList.remove("cutmode");
        }
    }

    private handleClick = (e: L.LeafletMouseEvent) => {
        this.props.featureClicked(e.latlng);
    };

    private pointToLayer = (mapFeature: geojson.Feature, latLng: L.LatLngExpression): L.Layer => {
        const radius = this.props.isSelected ? 7 : 5;
        return L.circleMarker(latLng, {className: 'pointMarker', ...this.getStyle(), radius: radius});
    };

    render(): React.ReactNode {
        return <GeoJSON data={this.props.data}
                        onclick={this.handleClick}
                        pointToLayer={this.pointToLayer}
                        style={this.getStyle}
                        ref={this.ref} />
    }
}
